/* FizzyIQ — Mission v2 statement (fizzy-msn2-mission).
   Lazy-attaches the production video's source, and pauses it off-screen.

   Why: the supplied clip is ~42MB. Left as a plain autoplay <video src>, every
   visit to this page downloads all of it — on cellular too — for a card that
   renders at 395x432. This is the same treatment the testimonial cards already
   use (preload=none + data-src, attached by IntersectionObserver when the card
   is near the viewport), so the page keeps one lazy-media convention.

   The poster still paints immediately, so the card is never empty, and a
   browser that blocks autoplay simply keeps showing it. */
(function () {
  'use strict';

  function init() {
    var vids = document.querySelectorAll('video[data-m2m-src]');
    if (!vids.length) return;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function attach(v) {
      if (v.getAttribute('src')) return;
      v.setAttribute('src', v.getAttribute('data-m2m-src'));
      /* Under reduced motion the clip is still available, it just does not
         start itself — the poster stands until the visitor presses play. */
      if (!reduce) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
    }

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(vids, attach);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting) {
          attach(v);
        } else if (!v.paused) {
          /* Nothing plays off-screen — it decodes frames for no one. */
          v.pause();
        }
      });
    }, { rootMargin: '300px 0px' });

    Array.prototype.forEach.call(vids, function (v) { io.observe(v); });

    /* Fallback, layered alongside the observer the way the sticky ATC and the
       floating header already do it in this theme. IntersectionObserver
       callbacks can fail to arrive — after a fast scroll, an iframe reflow, a
       restored bfcache page, or a section re-render in the editor — and when
       that happens the clip simply never gets a src and the card sits on its
       poster forever, which is indistinguishable from "the video broke".

       This costs one rect read per animation frame while any clip is still
       unattached, and unhooks itself the moment they all are. */
    function sweep() {
      var pending = 0;
      Array.prototype.forEach.call(vids, function (v) {
        if (v.getAttribute('src')) return;
        pending++;
        var r = v.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        if (r.bottom > -300 && r.top < vh + 300) { attach(v); pending--; }
      });
      if (!pending) {
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
      }
    }

    var ticking = false;
    function schedule() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; sweep(); });
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    /* One pass after layout settles, so a clip that is already on screen at
       load does not wait for the visitor to move. */
    requestAnimationFrame(sweep);
  }

  /* ── the headline scroll paint ──────────────────────────────────────
     Drives one custom property, --paint (0→1). The per-character maths lives
     in CSS; this only reports progress.

     Measured in DOCUMENT coordinates, not from the headline's viewport rect.
     That is the whole trick: the copy column is position:sticky, so once it
     pins, its getBoundingClientRect().top stops changing and a rect-based
     driver freezes the paint mid-sentence for the rest of the scroll. The
     section's own offset keeps moving whether anything inside it is pinned or
     not, so progress is taken from that instead.

     rAF-throttled, passive, and it writes only when the rounded value actually
     moves — a scroll that does not change it costs nothing. */
  function initPaint() {
    var heads = document.querySelectorAll('[data-m2m-paint]');
    if (!heads.length) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;   /* leave --paint at its fully-painted default */
    }

    var items = [];
    Array.prototype.forEach.call(heads, function (h) {
      var sec = h.closest('.m2m') || h.parentElement;
      items.push({
        el: h,
        sec: sec,
        copy: sec.querySelector('.m2m-copy'),
        media: sec.querySelector('.m2m-media'),
        start: (parseFloat(sec.getAttribute('data-paint-start')) || 80) / 100,
        travel: (parseFloat(sec.getAttribute('data-paint-travel')) || 45) / 100,
        last: -1
      });
    });

    function docTop(el) {
      var y = 0;
      while (el) { y += el.offsetTop; el = el.offsetParent; }
      return y;
    }

    var pending = false;
    function update() {
      pending = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var y = window.pageYOffset || document.documentElement.scrollTop;
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var p;

        /* Preferred: run the paint across exactly the stretch where the copy
           is PINNED. That is the whole point of the sticky column — the text
           holds still while the media slides past, and the sentence writes
           itself during that hold. Deriving the range from the pin instead of
           from an arbitrary slice of section height means the two can never
           drift apart: change the media column's height and the paint
           re-times itself. Finishing at 85% leaves a beat of complete,
           still text before the column releases. */
        var copy = it.copy;
        var sticky = copy && it.media && getComputedStyle(copy).position === 'sticky';
        if (sticky) {
          var stickyTop = parseFloat(getComputedStyle(copy).top) || 0;
          /* The copy CANNOT be its own geometric reference: offsetTop on a
             sticky element tracks the stuck shift, so it climbs with the
             scroll (measured: 1088 → 1216 → 1516 as it pinned) and pinStart
             would chase the scroll, freezing progress at zero the whole time
             it is pinned. Its grid sibling, the media column, is not sticky
             and shares the same row with align-items:start, so its document
             top IS the copy's natural top — and it stays put. */
          var pinStart = docTop(it.media) - stickyTop;
          var pinEnd = docTop(it.sec) + it.sec.offsetHeight - copy.offsetHeight - stickyTop;
          var pinSpan = (pinEnd - pinStart) * 0.85;
          if (pinSpan > 40) {
            p = (y - pinStart) / pinSpan;
          } else {
            sticky = false;   /* column too short to pin usefully — fall through */
          }
        }
        if (!sticky) {
          /* Stacked (<=1100) or nothing to pin against: fall back to progress
             through the section itself. */
          var span = Math.max(1, it.sec.offsetHeight * it.travel);
          p = (y + vh * it.start - docTop(it.sec)) / span;
        }
        p = p < 0 ? 0 : (p > 1 ? 1 : p);
        /* Three decimals: with ~120 characters, one character is ~0.008 of the
           range, so this resolves every glyph without thrashing style recalc. */
        var r = Math.round(p * 1000) / 1000;
        if (r !== it.last) { it.last = r; it.el.style.setProperty('--paint', r); }
      }
    }
    function schedule() { if (!pending) { pending = true; requestAnimationFrame(update); } }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    update();
  }

  function boot() { init(); initPaint(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

/* ── formula-iterations matrix card, ported from fizzy-sci-bento.js ──
   Self-contained: IO reveal (dot cascade + count-up) and the cursor-cluster
   hover. Namespaced m2m so the two sections can share a page. */
(function () {
  'use strict';
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function countUp(el) {
    var target = parseInt(el.textContent, 10);
    if (isNaN(target) || reduce) return;
    var start = null, dur = 1000;
    el.textContent = '0';
    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      el.textContent = Math.round((1 - Math.pow(1 - p, 3)) * target);
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function init(card) {
    if (card.dataset.m2mMatrixInit === '1') return;
    card.dataset.m2mMatrixInit = '1';

    function reveal() {
      card.classList.add('in-view');
      card.querySelectorAll('[data-m2m-count]').forEach(countUp);
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { reveal(); io.disconnect(); } });
      }, { threshold: 0.3 });
      io.observe(card);
      /* Fallback: contexts where the observer never fires (see the theme's
         preview note) still get the card revealed. */
      setTimeout(function () {
        var r = card.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) reveal();
      }, 1500);
    } else { reveal(); }

  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-m2m-matrix]'), init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
